import { useMemo } from "react";
import { generatePath, useParams } from "react-router-dom";

import type { ReferencesState } from "@contexts/References/References.types";
import { LayerTriple, User03, Users } from "@icons";
import { ROUTES } from "@router/router.constants";
import type { UrlParam } from "@router/router.types";
import {
  isContractRoute,
  isContractsRoute,
  isCustomerRoute,
  isCustomersRoute,
  isSiteRoute,
  isSitesRoute,
} from "@utils/perimeterRoute.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type {
  BreadcrumbKeys,
  PerimeterHeaderRoute,
} from "./PerimeterHeader.types";

export const getActiveTabValue = (pathname: string) => {
  if (isSitesRoute(pathname) || isSiteRoute(pathname)) {
    return ROUTES.PERIMETER_SITES;
  }

  if (isContractsRoute(pathname) || isContractRoute(pathname)) {
    return ROUTES.PERIMETER_CONTRACTS;
  }

  if (isCustomersRoute(pathname) || isCustomerRoute(pathname)) {
    return ROUTES.PERIMETER_CUSTOMERS;
  }

  return undefined;
};

export const useBuildPerimeterBreadcrumb = (
  pathname: string,
  references: ReferencesState,
) => {
  const { t } = useCustomTranslation("main");
  const params = useParams<UrlParam>();

  return useMemo(() => {
    // Render as function each time allow to render new reference we can modify
    // WARNING! If you want to change this, you need to check for exemple that there is no regression
    // when you go to a site detail page, and then click on a general perimeter like site, you should
    // have a breadcrumb that display its initial state without custom perimeter refs
    const correspondingArray = (): Record<
      BreadcrumbKeys,
      PerimeterHeaderRoute
    > => ({
      base: {
        pathName: t("perimeter.header.breadcrumb.base"),
        path: ROUTES.PERIMETER_CUSTOMERS,
        pathIcon: LayerTriple,
      },
      customers: {
        pathName: t("perimeter.header.breadcrumb.customers"),
        path: ROUTES.PERIMETER_CONTRACTS,
        pathIcon: LayerTriple,
      },
      contracts: {
        pathName: t("perimeter.header.breadcrumb.contracts"),
        path: ROUTES.PERIMETER_SITES,
        pathIcon: Users,
      },
      sites: {
        pathName: t("perimeter.header.breadcrumb.sites"),
        path: ROUTES.PERIMETER_SITE,
        pathIcon: User03,
      },
    });

    const initialState = [correspondingArray().base];
    const { customers, contracts, sites } = correspondingArray();

    if (isCustomersRoute(pathname)) {
      return initialState;
    }

    if (isContractsRoute(pathname) || isCustomerRoute(pathname)) {
      if (params?.customerId) {
        const { customerId } = params;

        customers.path = generatePath(ROUTES.PERIMETER_CONTRACTS_BY_CUSTOMER, {
          customerId,
        });
        customers.pathName = references.customer?.name ?? "...";
      }

      return [...initialState, customers];
    }

    if (isSitesRoute(pathname) || isContractRoute(pathname)) {
      if (params?.contractId) {
        const { customerId = "", contractId = "" } = params;

        customers.path = generatePath(ROUTES.PERIMETER_CONTRACTS_BY_CUSTOMER, {
          customerId,
        });
        customers.pathName = references.customer?.name ?? "...";

        contracts.path = generatePath(ROUTES.PERIMETER_SITES_BY_CONTRACT, {
          customerId,
          contractId,
        });
        contracts.pathName = references.contract?.name ?? "...";
      }

      return [...initialState, customers, contracts];
    }

    if (isSiteRoute(pathname)) {
      const {
        contractId = "",
        customerId = "",
        siteId = "",
        energy = "",
      } = params;

      customers.path = generatePath(ROUTES.PERIMETER_CONTRACTS_BY_CUSTOMER, {
        customerId,
      });
      customers.pathName = references.customer?.name ?? "...";

      contracts.path = generatePath(ROUTES.PERIMETER_SITES_BY_CONTRACT, {
        customerId,
        contractId,
      });
      contracts.pathName = references.contract?.name ?? "...";

      sites.path = generatePath(ROUTES.PERIMETER_SITE, {
        customerId,
        contractId,
        siteId,
        energy,
      });
      sites.pathName = references.site?.name ?? "...";

      return [...initialState, customers, contracts, sites];
    }

    return initialState;
  }, [pathname, references, params, t]);
};
